import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ROTAS } from '@remoto/shared';
import type { AnexoDaMensagem, DecisaoPermissao } from '@remoto/protocolo';
import { chamar } from '../api';
import { usarConversa } from '../sessoes/conversa';
import { filtrarPorExibicao, chavesQueRevelam, totalEscondido } from '../sessoes/exibicao';
import {
  gravarExibicao,
  guardarDitado,
  lerExibicao,
  recuperarDitado,
  type Exibicao,
} from '../workspace/memoria';
import { guardarMotorDaProsa } from './escolha';
import { usarSessaoMestre } from '../sessoes/mestre';
import { CampoDeEscolha } from '../componentes/campos';
import { ROTULO_DO_MOTOR, type Motor } from '@remoto/protocolo';
import { Bolha, PilhaDePedidos } from '../componentes/chat/pecas';
import { BotaoIcone, BotaoLeve } from '../componentes/layout';
import { IconeGrade, IconeMaisOpcoes } from '../componentes/icones';
import { CaixaDeMarcar } from '../componentes/campos';
import { usarTelaAcesa } from '../tela-acesa';
import { usarDitadoAoVivo } from '../audio/ditadoAoVivo';
import { duracaoLegivel } from '../rotas/audio';
import { Orbe } from './Orbe';
import { Microfone } from './Microfone';

/**
 * Prosa — a conversa de voz com a mestre.
 *
 * Cromo mínimo por fora e nada de novo por dentro: a conversa vem do mesmo
 * `usarConversa` do painel, as bolhas são as mesmas `Bolha`, e o cartão de
 * permissão é a mesma `PilhaDePedidos`. O que muda é o que se mostra e o
 * tamanho do que se mostra — não de onde vem.
 */
export function TelaDaProsa({
  sessaoId,
  aoSair,
  aoIrParaSessao,
  aoTrocarDeSessao,
}: {
  sessaoId: string;
  /** Volta para o painel de trabalho. */
  aoSair: () => void;
  aoIrParaSessao?: (id: string) => void;
  /** Trocar de mestre é trocar de conversa dentro da própria Prosa. */
  aoTrocarDeSessao?: (id: string) => void;
}): React.JSX.Element {
  const cliente = useQueryClient();
  const { sessao, pedidos, itens, carregarAnteriores, temMais, parcial } =
    usarConversa(sessaoId);

  /*
    Com dois motores há duas mestres, e a Prosa fala com UMA. A escolha fica
    guardada porque quem abre a Prosa está com as mãos ocupadas: perguntar toda
    vez seria um passo a mais no pior momento possível.
  */
  const { mestres } = usarSessaoMestre();
  const motorAtual = (mestres.find((m) => m.id === sessaoId)?.motor ?? 'CLAUDE') as Motor;

  function trocarDeMestre(motor: Motor): void {
    guardarMotorDaProsa(motor);
    const alvo = mestres.find((m) => (m.motor ?? 'CLAUDE') === motor);
    // Trocar de mestre é trocar de CONVERSA: a Prosa inteira é sobre uma
    // sessão, e navegar é o que faz a conversa, os pedidos e o ditado virem
    // da outra.
    if (alvo && alvo.id !== sessaoId) aoTrocarDeSessao?.(alvo.id);
  }

  const [exibicao, setExibicao] = useState<Exibicao>(() => lerExibicao('prosa'));
  function mudarExibicao(mudanca: Partial<Exibicao>): void {
    setExibicao((atual) => {
      const novo = { ...atual, ...mudanca };
      gravarExibicao(novo, 'prosa');
      return novo;
    });
  }

  // Quem fala com o telefone na mao nao quer a tela apagando no meio da frase.
  // O item existe no menu, entao ele tem de FAZER alguma coisa: opcao que nao
  // muda nada e pior que opcao ausente.
  usarTelaAcesa(exibicao.telaAcesa);

  const visiveis = useMemo(() => filtrarPorExibicao(itens, exibicao), [itens, exibicao]);
  const escondidos = useMemo(() => totalEscondido(itens, exibicao), [itens, exibicao]);

  const voz = usarDitadoAoVivo();
  const [erro, setErro] = useState('');
  /** O que o Whisper entendeu no último envio, para reler e corrigir. */
  const [ultimoDitado, setUltimoDitado] = useState(() => recuperarDitado(sessaoId));

  const enviar = useMutation({
    mutationFn: (texto: string) =>
      chamar(`${ROTAS.sessoes}/${sessaoId}/enviar`, {
        method: 'POST',
        corpo: { texto, anexos: [] as AnexoDaMensagem[] },
      }),
    onError: () => setErro('A máquina não recebeu a mensagem.'),
  });

  const responder = useMutation({
    mutationFn: (dados: { pedidoId: string; resposta: DecisaoPermissao }) =>
      chamar(`${ROTAS.permissoes}/${dados.pedidoId}/responder`, {
        method: 'POST',
        corpo: { pedidoId: dados.pedidoId, resposta: dados.resposta },
      }),
    onSettled: () => {
      void cliente.invalidateQueries({ queryKey: ['permissoes', sessaoId] });
      void cliente.invalidateQueries({ queryKey: ['permissoes-pendentes'] });
    },
    onError: () => setErro('Não foi possível responder.'),
  });

  const retomar = useMutation({
    mutationFn: () => chamar(`${ROTAS.sessoes}/${sessaoId}/retomar`, { method: 'POST' }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['sessao', sessaoId] }),
    onError: () => setErro('Não foi possível retomar esta conversa.'),
  });

  /**
   * Parar de falar manda — e a rede é escrita ANTES do despacho.
   *
   * Se a rede cair entre uma coisa e outra, o que a pessoa falou já está no
   * disco. Slot próprio, que o envio não limpa: o `onSuccess` do compositor
   * apaga o rascunho dele, e reusar aquele slot faria o envio limpar a própria
   * rede — em silêncio.
   */
  function mandar(texto: string): void {
    const limpo = texto.trim();
    if (!limpo) return;
    guardarDitado(sessaoId, limpo);
    setUltimoDitado(limpo);
    setErro('');
    enviar.mutate(limpo);
  }

  const dormindo = sessao?.estado === 'DORMINDO';
  const encerrada = sessao?.estado === 'ENCERRADA';

  /**
   * Ver a conversa sem parar de falar.
   *
   * Pedido do dono, literal: "enquanto está gravando, deixa uma opção para
   * clicar e arrastar de volta para o chat, para ver o chat enquanto está
   * falando... só para de gravar quando clicar no microfone". Então isto é
   * só o que se MOSTRA — não encosta no gravador.
   */
  const [verConversa, setVerConversa] = useState(false);
  /** Entre parar e o texto sair há espera real; a tela não pode ficar muda. */
  const [transcrevendo, setTranscrevendo] = useState(false);

  /**
   * O palco do microfone vale enquanto se GRAVA, e só.
   *
   * Era `voz.gravando || voz.parcial.length > 0`, e `parcial` nunca esvaziava:
   * `terminar()` guardava os segmentos e saía, então a Prosa ficava presa no
   * orbe para sempre — quem limpava era `comecar()`, na gravação seguinte. Ou
   * seja, só saía daquela tela quem gravasse de novo. Relato do dono, com dois
   * prints: "não faz o menor sentido ficar parada nessa tela de transcrição".
   */
  const emFala = voz.gravando && !verConversa;

  /** O arrasto que leva ao chat: Pointer Events cobrem dedo e mouse de uma vez. */
  const arrasto = useRef<number | null>(null);

  /** Toque-toque: o primeiro abre o microfone, o segundo para e manda. */
  async function tocarMicrofone(): Promise<void> {
    if (!voz.gravando) {
      setVerConversa(false);
      await voz.comecar();
      return;
    }
    setTranscrevendo(true);
    try {
      mandar(await voz.terminar());
    } finally {
      setTranscrevendo(false);
      setVerConversa(false);
    }
  }

  return (
    <div
      id="prosa"
      data-testid="prosa"
      className="flex h-dvh flex-col overflow-hidden bg-zinc-950 px-bordas font-sans text-zinc-100 antialiased"
    >
      {/* Barra de cima: só o nome e as duas saídas. */}
      <header className="flex shrink-0 items-center justify-between pt-entalhe">
        <span className="px-3 text-sm text-zinc-400">
          {sessao?.funcao === 'MESTRE' ? 'Mestre' : (sessao?.nome ?? 'Conversa')}
        </span>
        <div className="flex items-center gap-1 pr-1">
          {/*
            O seletor SÓ aparece quando há mais de uma: com um mestre só, ele
            seria um controle que não muda nada — e controle que não muda nada
            ensina a pessoa a ignorar os que mudam.
          */}
          {mestres.length > 1 ? (
            <CampoDeEscolha
              rotulo="Falar com"
              rotuloOculto
              compacto
              valor={motorAtual}
              opcoes={mestres.map((m) => ({
                id: (m.motor ?? 'CLAUDE') as Motor,
                rotulo: ROTULO_DO_MOTOR[(m.motor ?? 'CLAUDE') as Motor],
              }))}
              aoEscolher={trocarDeMestre}
            />
          ) : null}
          <MenuDaProsa exibicao={exibicao} aoMudar={mudarExibicao} />
        </div>
      </header>

      {/* O miolo: orbe e a fala virando texto grande, como na referência. */}
      {/*
        `justify-center` so quando a fala esta no centro da tela. Com a
        conversa, ela ocupa a altura inteira -- centralizar um filho que ja e
        `h-full` nao muda nada, mas `items-center` apertava a largura.
      */}
      <section
        className={
          'flex min-h-0 flex-1 flex-col gap-6 ' +
          (emFala ? 'items-center justify-center px-6' : 'px-3')
        }
      >
        {emFala ? (
          /*
            O palco do microfone. Arrastar para baixo leva ao chat SEM parar de
            gravar — Pointer Events, e não Touch Events: um código só cobre dedo
            e mouse, e é o que o ensaio consegue exercitar de verdade.
          */
          <div
            data-testid="prosa-palco"
            onPointerDown={(e) => {
              arrasto.current = e.clientY;
            }}
            onPointerUp={(e) => {
              const inicio = arrasto.current;
              arrasto.current = null;
              if (inicio !== null && Math.abs(e.clientY - inicio) > 60) setVerConversa(true);
            }}
            onPointerCancel={() => {
              arrasto.current = null;
            }}
            className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-6"
          >
            <Orbe nivel={voz.nivel} ouvindo={voz.gravando} />
            <p
              data-testid="prosa-transcricao"
              className="max-h-[38vh] overflow-y-auto text-center text-2xl leading-snug text-zinc-100"
            >
              {voz.parcial}
            </p>
            {/*
              O cronometro e o "quantos faltam" nao sao enfeite: entre apertar
              o microfone e o primeiro texto existe uma espera REAL (o cortador
              so fecha um trecho numa pausa depois do minimo). Sem um numero que
              anda, a tela parece morta -- e foi assim que o dono descreveu:
              "a transcricao nao aparece na hora".

              O orbe ja responde a voz, mas ele diz "estou ouvindo", nao "estou
              trabalhando e falta tanto". Numero que anda e a diferenca entre
              esperar e achar que travou.
            */}
            <div className="flex flex-col items-center gap-1">
              <span
                data-testid="prosa-cronometro"
                className="font-mono text-sm tabular-nums text-zinc-400"
              >
                {duracaoLegivel(voz.duracaoMs)}
              </span>
              <span
                className="text-xs uppercase tracking-widest text-zinc-500"
                aria-live="polite"
                data-testid="prosa-estado-voz"
              >
                {voz.pendentes > 0 ? `Ouvindo · transcrevendo ${voz.pendentes}` : 'Ouvindo'}
              </span>
            </div>
            {/*
              Gesto sozinho é affordance invisível: quem não adivinhar o arrasto
              fica preso aqui. O botão é a mesma porta, escrita na tela — o dono
              pediu "clicar E arrastar", nesta ordem.
            */}
            <BotaoLeve data-testid="prosa-ver-conversa" onClick={() => setVerConversa(true)}>
              ver a conversa
            </BotaoLeve>
          </div>
        ) : (
          <ConversaCurta
            itens={visiveis}
            escondidos={escondidos}
            aoRevelar={() =>
              mudarExibicao(
                Object.fromEntries(chavesQueRevelam(itens, exibicao).map((c) => [c, true])),
              )
            }
            aoIrParaSessao={aoIrParaSessao}
            sessaoId={sessaoId}
            /*
              Sem o id da máquina a `Bolha` devolve `null` para imagem de
              arquivo do projeto — em silêncio, sem erro no console. Era o
              segundo motivo de o `Read` de imagem não aparecer aqui.
            */
            maquinaId={sessao?.maquina?.id ?? ''}
            parcial={parcial}
            temMais={temMais}
            aoCarregarAnteriores={() => void carregarAnteriores()}
          />
        )}
      </section>

      {/*
        O cartão de permissão NUNCA passa pelo filtro de exibição: uma sessão
        parada num cartão invisível é impasse mudo. Teto em `vh` e rodapé fixo,
        como manda a regra do front.
      */}
      {pedidos.length > 0 ? (
        <div className="shrink-0 px-3">
          <PilhaDePedidos
            pedidos={pedidos}
            ocupado={responder.isPending}
            aoDecidir={(pedidoId, resposta) => responder.mutate({ pedidoId, resposta })}
          />
        </div>
      ) : null}

      {/* Erro tem lugar: nada é engolido. */}
      {erro || voz.erro ? (
        <p className="shrink-0 px-4 pb-1 text-center text-xs text-amber-400" role="alert">
          {erro || voz.erro}
        </p>
      ) : null}

      {dormindo ? (
        <div className="shrink-0 px-4 pb-2 text-center text-sm text-zinc-400">
          Esta conversa está dormindo.{' '}
          <BotaoLeve onClick={() => retomar.mutate()} disabled={retomar.isPending}>
            Retomar
          </BotaoLeve>
        </div>
      ) : null}

      {/*
        Falando COM a conversa na tela: a faixa é a prova de que o microfone
        continua aberto, e o cronômetro é o que a torna crível. Botão vermelho
        aceso sem número que anda é exatamente o que faz a pessoa duvidar e
        tocar de novo — e o toque a mais aqui PARA a gravação.

        Ela também é o caminho de volta ao orbe, num toque.
      */}
      {voz.gravando && verConversa ? (
        <button
          onClick={() => setVerConversa(false)}
          data-testid="prosa-faixa-voz"
          title="voltar para o microfone"
          /*
            `red-400`, e não `red-300`/`red-950`: o tema claro só remapeia os
            tons que têm significado, e `red-400` é um deles (vira #b91c1c).
            A primeira versão usava `text-red-300` sobre `bg-red-950/30` — no
            escuro ficava bom, e no claro virava vermelho claro sobre rosa,
            ilegível. Foi o print que contou; o ensaio estava verde.
          */
          className="mx-4 mb-1 flex shrink-0 items-center justify-center gap-2 rounded-md border border-red-400/50 bg-red-400/10 px-3 py-1 text-xs font-medium text-red-400 pointer-coarse:min-h-11"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-400" />
          Ouvindo · <span className="font-mono tabular-nums">{duracaoLegivel(voz.duracaoMs)}</span>
        </button>
      ) : null}

      {/*
        A espera entre parar e o texto sair é REAL: o último trecho só é
        transcrito depois do `finalizar`, e `terminar()` tolera até 20 voltas de
        700 ms. Devolver a conversa e não dizer nada nesse intervalo trocaria
        "tela presa" por "tela que não respondeu ao toque" — e a segunda faz a
        pessoa tocar de novo.
      */}
      {transcrevendo ? (
        <p
          data-testid="prosa-transcrevendo"
          aria-live="polite"
          className="shrink-0 px-4 pb-1 text-center text-xs uppercase tracking-widest text-zinc-500"
        >
          transcrevendo…
        </p>
      ) : null}

      {ultimoDitado && !voz.gravando ? (
        <button
          onClick={() => mandar(ultimoDitado)}
          data-testid="prosa-ultimo-ditado"
          className="mx-4 mb-1 shrink-0 truncate rounded-md border border-zinc-800 px-3 py-1 text-left text-xs text-zinc-500"
          title="O que eu entendi da última vez — toque para corrigir e mandar de novo"
        >
          o que eu entendi: {ultimoDitado}
        </button>
      ) : null}

      {/* Embaixo: o microfone grande, e a saída no canto, como na referência. */}
      <footer className="flex shrink-0 items-center justify-between px-4 pb-2 pb-gesto">
        <span className="w-11" />
        <Microfone
          gravando={voz.gravando}
          bloqueado={
            encerrada
              ? 'esta conversa foi encerrada'
              : dormindo
                ? 'acorde a conversa antes de falar'
                : undefined
          }
          aoTocar={() => void tocarMicrofone()}
        />
        <BotaoIcone rotulo="Painel" onClick={aoSair} data-testid="prosa-sair">
          <IconeGrade />
        </BotaoIcone>
      </footer>
    </div>
  );
}

/** O fio de conversa, em balões curtos. */
function ConversaCurta({
  itens,
  escondidos,
  aoRevelar,
  aoIrParaSessao,
  sessaoId,
  maquinaId,
  parcial,
  temMais,
  aoCarregarAnteriores,
}: {
  itens: ReturnType<typeof filtrarPorExibicao>;
  escondidos: number;
  aoRevelar: () => void;
  aoIrParaSessao?: (id: string) => void;
  /** Os dois que a imagem precisa: anexo vem por sessão, arquivo vem por máquina. */
  sessaoId: string;
  maquinaId: string;
  /** A resposta sendo escrita agora; vazio quando nao ha nenhuma. */
  parcial: string;
  temMais: boolean;
  aoCarregarAnteriores: () => void;
}): React.JSX.Element {
  const caixa = useRef<HTMLDivElement>(null);
  /** Quantos itens ja estavam desenhados quando a rolagem foi ajustada. */
  const grudado = useRef(true);

  /**
   * Chat abre no FIM, e acompanha o fim enquanto voce esta nele.
   *
   * Medido: sem isto a conversa abria em `scrollTop: 0`, ou seja na mensagem
   * mais ANTIGA -- e o ensaio nao pegou, porque `toBeVisible` do Playwright diz
   * que o elemento existe e tem caixa, nao que ele esta dentro do recorte que
   * rola. A asserção passou com a mensagem fora da tela.
   *
   * `grudado` guarda a mesma regra do painel: quem rolou para tras fica onde
   * estava, porque ser puxado de volta a cada mensagem nova e insuportavel.
   */
  useEffect(() => {
    const no = caixa.current;
    if (!no || !grudado.current) return;
    no.scrollTop = no.scrollHeight;
  }, [itens.length]);

  return (
    <div
      ref={caixa}
      data-testid="prosa-conversa"
      onScroll={(e) => {
        const n = e.currentTarget;
        grudado.current = n.scrollHeight - n.scrollTop - n.clientHeight < 120;
      }}
      className="h-full w-full overflow-y-auto overscroll-contain py-4"
    >
      {/*
        `mt-auto` no miolo, e NAO `justify-end` no pai que rola.

        Dois defeitos medidos vieram daquele `justify-end`. O primeiro e que os
        baloes sao itens flex e encolhem por padrao: 48 mensagens eram
        ESPREMIDAS em 736 px em vez de transbordar, e `scrollHeight` ficava
        igual a `clientHeight` -- nao havia o que rolar porque nada passava.
        O segundo aparece quando o primeiro e resolvido: com `justify-end`, o
        conteudo que sobra transborda para CIMA, e `scrollTop` nao vai a
        negativo -- o comeco da conversa fica inalcancavel.

        `mt-auto` faz as duas coisas certas de uma vez: com pouca conversa ela
        assenta embaixo (que e o que se espera de um chat), e com muita o
        automatico vira zero e a rolagem cobre tudo, do primeiro ao ultimo.
      */}
      <div className="mt-auto flex min-h-full w-full flex-col justify-end gap-2">
        {temMais ? (
          <button
            onClick={aoCarregarAnteriores}
            className="mx-auto shrink-0 text-xs text-zinc-600 hover:text-zinc-400 pointer-coarse:min-h-11"
          >
            ver o que veio antes
          </button>
        ) : null}
        {/*
          Sem `slice`. A primeira versao mostrava so as ultimas 30 e o resto
          sumia calado -- o mesmo defeito que o chip de escondidos existe para
          nao cometer. Quem limita quanto vem e a pagina do `usarConversa`, e o
          que veio, aparece.
        */}
        {itens.map((item) => (
          <div key={item.chave} className="w-full shrink-0">
            <Bolha
              item={item}
              aoIrParaSessao={aoIrParaSessao}
              sessaoId={sessaoId}
              maquinaId={maquinaId}
            />
          </div>
        ))}
      {/*
        A marca de elisão. Esconder sem dizer o quanto é a armadilha que este
        projeto já pagou caro: informação que some sem aviso. O chip diz quantos
        e leva a eles num toque.
      */}
        {/*
          A resposta crescendo. Bolha provisoria, marcada como tal, e o
          `usarConversa` a descarta quando a versao do transcript chega.

          Sem pedaco nenhum, isto NAO aparece -- e o que se ve e a pilha de
          pontinhos, que diz "estou pensando" sem inventar texto.
        */}
        {parcial ? (
          <div
            data-testid="prosa-parcial"
            className="w-full shrink-0 rounded-lg bg-zinc-900/60 px-3 py-2 text-sm text-zinc-200"
          >
            {parcial}
            <span className="ml-1 inline-block h-3 w-1.5 animate-pulse bg-zinc-400 align-middle" />
          </div>
        ) : null}

        {escondidos > 0 ? (
          <button
            onClick={aoRevelar}
            data-testid="prosa-escondidos"
            className="mx-auto shrink-0 rounded-full border border-zinc-800 px-3 py-1 text-xs text-zinc-500 hover:text-zinc-300 pointer-coarse:min-h-11"
          >
            {escondidos === 1 ? '1 item escondido' : `${escondidos} itens escondidos`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** O ⋯ da Prosa: o mesmo mecanismo do painel, com o perfil da Prosa. */
function MenuDaProsa({
  exibicao,
  aoMudar,
}: {
  exibicao: Exibicao;
  aoMudar: (m: Partial<Exibicao>) => void;
}): React.JSX.Element {
  const opcoes: { chave: keyof Exibicao; rotulo: string }[] = [
    { chave: 'ferramentas', rotulo: 'comandos e resultados' },
    { chave: 'pensamento', rotulo: 'pensamento da IA' },
    { chave: 'tarefas', rotulo: 'lista de tarefas' },
    { chave: 'telaAcesa', rotulo: 'manter a tela acesa' },
  ];
  return (
    <details className="relative">
      <summary
        title="O que mostrar na conversa"
        data-testid="prosa-menu"
        className="flex cursor-pointer list-none items-center justify-center rounded text-zinc-500 hover:text-zinc-200 pointer-coarse:h-11 pointer-coarse:w-11 [&::-webkit-details-marker]:hidden"
      >
        <IconeMaisOpcoes />
      </summary>
      <div className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-zinc-700 bg-zinc-900 p-2 text-xs shadow-lg shadow-black/40">
        {opcoes.map((o) => (
          <CaixaDeMarcar
            key={o.chave}
            marcada={Boolean(exibicao[o.chave])}
            aoMarcar={(v: boolean) => aoMudar({ [o.chave]: v })}
            className="py-1"
          >
            <span className="text-zinc-300">{o.rotulo}</span>
          </CaixaDeMarcar>
        ))}
      </div>
    </details>
  );
}
